import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { toast } from 'sonner';
import { 
  RotateCcw,
  Loader2,
  ImageIcon,
  Pencil,
  Phone,
  Mail,
  Globe,
  Save,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebsiteManageDialog = ({ 
  open, 
  onOpenChange, 
  website, 
  onSuccess 
}) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  
  const [activeTab, setActiveTab] = useState('options');
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  
  // Full regeneration state
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  
  // Category/Image fix state
  const [newCategory, setNewCategory] = useState('');
  
  // Custom image upload state
  const [customImages, setCustomImages] = useState({
    hero: null,
    services: []
  });
  const [existingCustomImages, setExistingCustomImages] = useState(null);
  const heroInputRef = useRef(null);
  const serviceInputRef = useRef(null);
  
  // Content editing state
  const [content, setContent] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open && website) {
      setActiveTab('options');
      setRegenerateInstructions('');
      setNewCategory('');
      setContent(null);
      setExpandedSections({});
      setCustomImages({ hero: null, services: [] });
      setExistingCustomImages(null);
      // Fetch existing custom images
      fetchExistingImages();
    }
  }, [open, website]);
  
  // Fetch existing custom images
  const fetchExistingImages = async () => {
    if (!website) return;
    try {
      const response = await axios.get(
        `${API}/websites/${website.id}/images`,
        { headers }
      );
      setExistingCustomImages(response.data.custom_images || {});
    } catch (error) {
      console.error('Failed to fetch existing images:', error);
    }
  };
  
  // Handle file upload and convert to base64
  const handleFileUpload = async (file, type, index = null) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      
      if (type === 'hero') {
        setCustomImages(prev => ({ ...prev, hero: base64 }));
      } else if (type === 'service' && index !== null) {
        setCustomImages(prev => {
          const newServices = [...prev.services];
          newServices[index] = base64;
          return { ...prev, services: newServices };
        });
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Upload custom images to backend
  const handleUploadImages = async () => {
    if (!customImages.hero && customImages.services.filter(Boolean).length === 0) {
      toast.error('Please select at least one image to upload');
      return;
    }
    
    setLoading(true);
    try {
      await axios.patch(
        `${API}/websites/${website.id}/images`,
        {
          hero_image: customImages.hero,
          // Send full array with nulls to preserve positions (e.g., [null, "img2", "img3"] keeps slot indices)
          service_images: customImages.services
        },
        { headers }
      );
      toast.success('Custom images uploaded successfully!');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setLoading(false);
    }
  };
  
  // Remove custom image
  const handleRemoveCustomImage = async (imageType) => {
    setLoading(true);
    try {
      await axios.delete(
        `${API}/websites/${website.id}/images/${imageType}`,
        { headers }
      );
      toast.success('Custom image removed, reverted to Unsplash');
      setExistingCustomImages(prev => {
        const updated = { ...prev };
        delete updated[imageType];
        return updated;
      });
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to remove image');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch content when switching to edit tab
  const fetchContent = async () => {
    if (!website || content) return;
    
    setContentLoading(true);
    try {
      const response = await axios.get(
        `${API}/websites/${website.id}/content`,
        { headers }
      );
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load website content');
    } finally {
      setContentLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === 'edit' && !content) {
      fetchContent();
    }
  };
  
  // Full regeneration
  const handleFullRegenerate = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/websites/regenerate`,
        { 
          website_id: website.id,
          extra_instructions: regenerateInstructions
        },
        { headers }
      );
      toast.success('Website regenerated successfully!');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to regenerate website');
    } finally {
      setLoading(false);
    }
  };
  
  // Category/Image fix
  const handleCategoryUpdate = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category');
      return;
    }
    
    setLoading(true);
    try {
      await axios.patch(
        `${API}/websites/${website.id}/category`,
        { 
          category: newCategory,
          regenerate_images: true
        },
        { headers }
      );
      toast.success('Images updated successfully!');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to update images');
    } finally {
      setLoading(false);
    }
  };
  
  // Content update
  const handleContentSave = async () => {
    if (!content) return;
    
    setLoading(true);
    try {
      await axios.patch(
        `${API}/websites/${website.id}/content`,
        {
          tagline: content.tagline,
          about_paragraph: content.about_paragraph,
          phone: content.phone,
          business_email: content.business_email,
          social_links: content.social_links,
          hero_features: content.hero_features,
          services: content.services,
          faqs: content.faqs,
          why_us: content.why_us,
          process_steps: content.process_steps,
          cta_title: content.cta_title,
          cta_subtitle: content.cta_subtitle,
          cta_button: content.cta_button,
          sections_enabled: content.sections_enabled
        },
        { headers }
      );
      toast.success('Content updated successfully!');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const updateContentField = (field, value) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const updateNestedField = (field, index, key, value) => {
    setContent(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [key]: value } : item
      )
    }));
  };
  
  const toggleSectionEnabled = (section) => {
    setContent(prev => ({
      ...prev,
      sections_enabled: {
        ...prev.sections_enabled,
        [section]: !prev.sections_enabled?.[section]
      }
    }));
  };
  
  const addArrayItem = (field, template) => {
    setContent(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), template]
    }));
  };
  
  const removeArrayItem = (field, index) => {
    setContent(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (!website) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Manage Website - {website.business_name}
          </DialogTitle>
          <DialogDescription>
            Choose how you want to update this website
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="options" className="text-xs sm:text-sm" data-testid="tab-options">
              Options
            </TabsTrigger>
            <TabsTrigger value="images" className="text-xs sm:text-sm" data-testid="tab-images">
              Fix Images
            </TabsTrigger>
            <TabsTrigger value="edit" className="text-xs sm:text-sm" data-testid="tab-edit">
              Edit Content
            </TabsTrigger>
          </TabsList>
          
          {/* Options Tab - Full Regeneration */}
          <TabsContent value="options" className="flex-1 overflow-y-auto space-y-4 mt-4">
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4 border border-white/10">
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <RotateCcw className="w-4 h-4 text-primary" />
                  Full Theme Regeneration
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Regenerate the entire website with new AI-generated content. Use instructions to customize the style and content.
                </p>
                
                {/* Style Presets */}
                <div className="space-y-3 mb-4">
                  <Label className="text-sm font-medium">Style Presets</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Modern & Minimal', value: 'Make it modern and minimal with clean lines' },
                      { label: 'Bold & Vibrant', value: 'Use bold colors and vibrant energy' },
                      { label: 'Professional', value: 'Keep it professional and corporate' },
                      { label: 'Warm & Friendly', value: 'Make it warm and welcoming' },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`justify-start text-left h-auto py-2 px-3 text-xs ${
                          regenerateInstructions.includes(preset.value) 
                            ? 'border-primary bg-primary/10' 
                            : ''
                        }`}
                        onClick={() => {
                          if (regenerateInstructions.includes(preset.value)) {
                            setRegenerateInstructions(prev => prev.replace(preset.value, '').trim());
                          } else {
                            setRegenerateInstructions(prev => prev ? `${prev}. ${preset.value}` : preset.value);
                          }
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Additional Instructions</Label>
                  <Textarea
                    placeholder="Describe how you want to change the website..."
                    value={regenerateInstructions}
                    onChange={(e) => setRegenerateInstructions(e.target.value)}
                    rows={3}
                    className="text-sm"
                    data-testid="regen-instructions"
                  />
                </div>
                
                <Button
                  className="w-full mt-4"
                  onClick={handleFullRegenerate}
                  disabled={loading}
                  data-testid="btn-full-regen"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Full Regeneration
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          {/* Images Tab - Category Fix + Custom Upload */}
          <TabsContent value="images" className="flex-1 overflow-y-auto space-y-4 mt-4">
            {/* Custom Image Upload Section */}
            <div className="bg-muted/30 rounded-lg p-4 border border-white/10">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <Upload className="w-4 h-4 text-primary" />
                Upload Custom Images
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                Upload your own images to use instead of stock photos. Images will be stored for this website.
              </p>
              
              {/* Existing custom images */}
              {existingCustomImages && (existingCustomImages.hero || existingCustomImages.services?.length > 0) && (
                <div className="mb-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <p className="text-sm text-green-600 font-medium mb-2">Currently using custom images:</p>
                  <div className="flex flex-wrap gap-2">
                    {existingCustomImages.hero && (
                      <div className="flex items-center gap-1 bg-background/50 px-2 py-1 rounded text-xs">
                        <span>Hero Image</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleRemoveCustomImage('hero')}
                          disabled={loading}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    {existingCustomImages.services?.length > 0 && (
                      <div className="flex items-center gap-1 bg-background/50 px-2 py-1 rounded text-xs">
                        <span>{existingCustomImages.services.length} Service Image(s)</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleRemoveCustomImage('services')}
                          disabled={loading}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {/* Hero Image Upload */}
                <div>
                  <Label className="text-sm">Hero Image (Main Banner)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      ref={heroInputRef}
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'hero')}
                    />
                    {customImages.hero ? (
                      <div className="relative w-full h-32 rounded-lg overflow-hidden border border-white/10">
                        <img 
                          src={customImages.hero} 
                          alt="Hero preview" 
                          className="w-full h-full object-cover"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 h-7 w-7 p-0"
                          onClick={() => setCustomImages(prev => ({ ...prev, hero: null }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full h-20 border-dashed"
                        onClick={() => heroInputRef.current?.click()}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5" />
                          <span className="text-xs">Click to upload hero image</span>
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Service Images Upload */}
                <div>
                  <Label className="text-sm">Service Images (up to 6)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    These will replace Unsplash images for your services in order
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div key={index}>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id={`service-upload-${index}`}
                          onChange={(e) => handleFileUpload(e.target.files[0], 'service', index)}
                        />
                        {customImages.services[index] ? (
                          <div className="relative h-20 rounded-lg overflow-hidden border border-white/10">
                            <img 
                              src={customImages.services[index]} 
                              alt={`Service ${index + 1}`} 
                              className="w-full h-full object-cover"
                            />
                            <Button
                              variant="destructive"
                              size="sm"
                              className="absolute top-1 right-1 h-5 w-5 p-0"
                              onClick={() => {
                                setCustomImages(prev => {
                                  const newServices = [...prev.services];
                                  newServices[index] = null;
                                  return { ...prev, services: newServices };
                                });
                              }}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <label
                            htmlFor={`service-upload-${index}`}
                            className="flex flex-col items-center justify-center h-20 rounded-lg border border-dashed border-white/20 cursor-pointer hover:border-primary/50 transition-colors"
                          >
                            <Plus className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">#{index + 1}</span>
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <Button
                className="w-full mt-4"
                onClick={handleUploadImages}
                disabled={loading || (!customImages.hero && customImages.services.filter(Boolean).length === 0)}
                data-testid="btn-upload-images"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Custom Images
                  </>
                )}
              </Button>
            </div>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or use stock photos</span>
              </div>
            </div>
            
            {/* Category Fix Section */}
            <div className="bg-muted/30 rounded-lg p-4 border border-white/10">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-primary" />
                Fix Images / Change Category
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                If the images don't match your business type, enter a better category keyword to search for relevant images on Unsplash.
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Current Category</Label>
                  <p className="text-sm text-muted-foreground bg-background/50 px-3 py-2 rounded mt-1">
                    {website?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 
                     website?.primary_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 
                     'Unknown'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">New Category / Image Search</Label>
                  <Input
                    placeholder="e.g., pet store, decorating, florist, bakery..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="text-sm"
                    data-testid="new-category"
                  />
                  <p className="text-xs text-muted-foreground">
                    This will search Unsplash for images matching this keyword
                  </p>
                </div>
                
                <div className="pt-2">
                  <Label className="text-sm mb-2 block">Common Categories</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'restaurant', 'cafe', 'salon', 'spa', 'gym', 'bakery',
                      'florist', 'pet store', 'decorator', 'plumber', 'electrician',
                      'dentist', 'hotel', 'pub', 'bar', 'retail shop'
                    ].map((cat) => (
                      <Button
                        key={cat}
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`h-auto py-1 px-2 text-xs ${
                          newCategory === cat ? 'border-primary bg-primary/10' : ''
                        }`}
                        onClick={() => setNewCategory(cat)}
                      >
                        {cat}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              
              <Button
                className="w-full mt-4"
                onClick={handleCategoryUpdate}
                disabled={loading || !newCategory.trim()}
                data-testid="btn-update-images"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating Images...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Update from Unsplash
                  </>
                )}
              </Button>
            </div>
          </TabsContent>
          
          {/* Edit Content Tab */}
          <TabsContent value="edit" className="flex-1 overflow-y-auto space-y-4 mt-4">
            {contentLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : content ? (
              <div className="space-y-4">
                {/* Contact Information */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('contact')}
                  >
                    <span className="font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4 text-primary" />
                      Contact Information
                    </span>
                    {expandedSections.contact ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.contact && (
                    <div className="px-4 pb-4 space-y-3">
                      <div>
                        <Label className="text-sm">Phone Number</Label>
                        <Input
                          value={content.phone || ''}
                          onChange={(e) => updateContentField('phone', e.target.value)}
                          placeholder="01onal xxx xxxx"
                          className="text-sm mt-1"
                          data-testid="edit-phone"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Business Email</Label>
                        <Input
                          type="email"
                          value={content.business_email || ''}
                          onChange={(e) => updateContentField('business_email', e.target.value)}
                          placeholder="contact@business.com"
                          className="text-sm mt-1"
                          data-testid="edit-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Social Links</Label>
                        <Input
                          value={content.social_links?.facebook || ''}
                          onChange={(e) => updateContentField('social_links', { ...content.social_links, facebook: e.target.value })}
                          placeholder="Facebook URL"
                          className="text-sm"
                        />
                        <Input
                          value={content.social_links?.instagram || ''}
                          onChange={(e) => updateContentField('social_links', { ...content.social_links, instagram: e.target.value })}
                          placeholder="Instagram URL"
                          className="text-sm"
                        />
                        <Input
                          value={content.social_links?.twitter || ''}
                          onChange={(e) => updateContentField('social_links', { ...content.social_links, twitter: e.target.value })}
                          placeholder="Twitter/X URL"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Hero Section */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('hero')}
                  >
                    <span className="font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Hero Section
                    </span>
                    {expandedSections.hero ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.hero && (
                    <div className="px-4 pb-4 space-y-3">
                      <div>
                        <Label className="text-sm">Tagline</Label>
                        <Input
                          value={content.tagline || ''}
                          onChange={(e) => updateContentField('tagline', e.target.value)}
                          placeholder="Your compelling tagline"
                          className="text-sm mt-1"
                          data-testid="edit-tagline"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Hero Features (badges below tagline)</Label>
                        <div className="space-y-2 mt-1">
                          {(content.hero_features || []).map((feature, idx) => (
                            <div key={idx} className="flex gap-2">
                              <Input
                                value={feature}
                                onChange={(e) => {
                                  const newFeatures = [...content.hero_features];
                                  newFeatures[idx] = e.target.value;
                                  updateContentField('hero_features', newFeatures);
                                }}
                                className="text-sm flex-1"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newFeatures = content.hero_features.filter((_, i) => i !== idx);
                                  updateContentField('hero_features', newFeatures);
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateContentField('hero_features', [...(content.hero_features || []), ''])}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Feature
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* About Section */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('about')}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary" />
                        About Section
                      </span>
                      <Switch
                        checked={content.sections_enabled?.about !== false}
                        onCheckedChange={() => toggleSectionEnabled('about')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {expandedSections.about ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.about && (
                    <div className="px-4 pb-4">
                      <Label className="text-sm">About Paragraph</Label>
                      <Textarea
                        value={content.about_paragraph || ''}
                        onChange={(e) => updateContentField('about_paragraph', e.target.value)}
                        placeholder="Tell customers about your business..."
                        rows={4}
                        className="text-sm mt-1"
                        data-testid="edit-about"
                      />
                    </div>
                  )}
                </div>
                
                {/* Services Section */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('services')}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-primary" />
                        Services ({(content.services || []).length})
                      </span>
                      <Switch
                        checked={content.sections_enabled?.services !== false}
                        onCheckedChange={() => toggleSectionEnabled('services')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {expandedSections.services ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.services && (
                    <div className="px-4 pb-4 space-y-4">
                      {(content.services || []).map((service, idx) => (
                        <div key={idx} className="bg-background/50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Service {idx + 1}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeArrayItem('services', idx)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          <Input
                            value={service.name || ''}
                            onChange={(e) => updateNestedField('services', idx, 'name', e.target.value)}
                            placeholder="Service name"
                            className="text-sm"
                          />
                          <Textarea
                            value={service.description || ''}
                            onChange={(e) => updateNestedField('services', idx, 'description', e.target.value)}
                            placeholder="Service description"
                            rows={2}
                            className="text-sm"
                          />
                          <Input
                            value={service.icon || ''}
                            onChange={(e) => updateNestedField('services', idx, 'icon', e.target.value)}
                            placeholder="Icon emoji (e.g., ✂️)"
                            className="text-sm w-24"
                          />
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('services', { name: '', description: '', icon: '' })}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Service
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* FAQs Section */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('faqs')}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium flex items-center gap-2">
                        <Mail className="w-4 h-4 text-primary" />
                        FAQs ({(content.faqs || []).length})
                      </span>
                      <Switch
                        checked={content.sections_enabled?.faqs !== false}
                        onCheckedChange={() => toggleSectionEnabled('faqs')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {expandedSections.faqs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.faqs && (
                    <div className="px-4 pb-4 space-y-4">
                      {(content.faqs || []).map((faq, idx) => (
                        <div key={idx} className="bg-background/50 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">FAQ {idx + 1}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeArrayItem('faqs', idx)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          <Input
                            value={faq.question || ''}
                            onChange={(e) => updateNestedField('faqs', idx, 'question', e.target.value)}
                            placeholder="Question"
                            className="text-sm"
                          />
                          <Textarea
                            value={faq.answer || ''}
                            onChange={(e) => updateNestedField('faqs', idx, 'answer', e.target.value)}
                            placeholder="Answer"
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('faqs', { question: '', answer: '' })}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add FAQ
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* CTA Section */}
                <div className="bg-muted/30 rounded-lg border border-white/10">
                  <button
                    className="w-full p-4 flex items-center justify-between"
                    onClick={() => toggleSection('cta')}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Call to Action
                      </span>
                      <Switch
                        checked={content.sections_enabled?.cta !== false}
                        onCheckedChange={() => toggleSectionEnabled('cta')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {expandedSections.cta ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.cta && (
                    <div className="px-4 pb-4 space-y-3">
                      <div>
                        <Label className="text-sm">CTA Title</Label>
                        <Input
                          value={content.cta_title || ''}
                          onChange={(e) => updateContentField('cta_title', e.target.value)}
                          placeholder="Ready to get started?"
                          className="text-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">CTA Subtitle</Label>
                        <Input
                          value={content.cta_subtitle || ''}
                          onChange={(e) => updateContentField('cta_subtitle', e.target.value)}
                          placeholder="Contact us today..."
                          className="text-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Button Text</Label>
                        <Input
                          value={content.cta_button || ''}
                          onChange={(e) => updateContentField('cta_button', e.target.value)}
                          placeholder="Get Started"
                          className="text-sm mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Save Button */}
                <Button
                  className="w-full"
                  onClick={handleContentSave}
                  disabled={loading}
                  data-testid="btn-save-content"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Unable to load content. Please try again.</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={fetchContent}>
                  Retry
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default WebsiteManageDialog;
